import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { tutorAPI, reviewAPI, messageAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { formatNextAvailable, getAvailabilityDisplay } from '../lib/availabilityUtils';
import { 
  GraduationCap, 
  Star, 
  Clock, 
  DollarSign,
  ArrowLeft,
  Loader2,
  Calendar,
  MessageSquare,
  BookOpen,
  Award,
  CheckCircle,
  MapPin,
  Globe,
  Play,
  User,
  Briefcase
} from 'lucide-react';

const TutorProfilePage = () => {
  const { tutorId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [tutor, setTutor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messageOpen, setMessageOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  useEffect(() => {
    loadTutor();
  }, [tutorId]);

  const loadTutor = async () => {
    try {
      const response = await tutorAPI.get(tutorId);
      setTutor(response.data);
    } catch (error) {
      console.error('Error loading tutor:', error);
      toast.error('Tutor not found');
      navigate('/tutors');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!message.trim()) return;
    
    setSendingMessage(true);
    try {
      await messageAPI.send({ recipient_id: tutorId, content: message });
      toast.success('Message sent!');
      setMessage('');
      setMessageOpen(false);
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleBookSession = () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/book/${tutorId}` } });
    } else {
      navigate(`/book/${tutorId}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-indigo-700" />
      </div>
    );
  }

  if (!tutor) return null;

  const gradeLevelNames = {
    high_school: 'High School',
    university: 'University/College',
    graduate: 'Graduate Level'
  };

  // Extract video ID from YouTube URL if present
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : null;
  };

  const videoEmbedUrl = getYouTubeEmbedUrl(tutor.intro_video_url);

  return (
    <div className="min-h-screen bg-stone-50" data-testid="tutor-profile-page">
      {/* Header */}
      <header className="bg-white border-b border-stone-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/tutors')} data-testid="back-btn">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <Link to="/" className="flex items-center gap-2">
                <GraduationCap className="h-8 w-8 text-indigo-700" />
                <span className="font-heading text-xl font-semibold text-slate-900 hidden sm:block">
                  Gifted Academy
                </span>
              </Link>
            </div>
            <div className="flex items-center gap-4">
              {isAuthenticated ? (
                <Dialog open={messageOpen} onOpenChange={setMessageOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="rounded-full gap-2" data-testid="message-btn">
                      <MessageSquare className="h-4 w-4" />
                      Message
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Send Message to {tutor.name}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <Textarea
                        placeholder="Write your message..."
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        rows={4}
                        data-testid="message-textarea"
                      />
                      <Button 
                        onClick={handleSendMessage} 
                        disabled={sendingMessage || !message.trim()}
                        className="w-full rounded-full"
                        data-testid="send-message-btn"
                      >
                        {sendingMessage ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                        Send Message
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              ) : (
                <Link to="/login">
                  <Button variant="outline" className="rounded-full">Sign In</Button>
                </Link>
              )}
              <Button 
                onClick={handleBookSession}
                className="rounded-full bg-slate-900 hover:bg-slate-800 gap-2"
                data-testid="book-session-btn"
              >
                <Calendar className="h-4 w-4" />
                Book Session
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Profile Header Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="border-stone-100 overflow-hidden">
                <CardContent className="p-0">
                  <div className="bg-gradient-to-r from-indigo-600 to-indigo-800 h-32" />
                  <div className="px-8 pb-8 -mt-16">
                    <div className="flex flex-col sm:flex-row items-start gap-6">
                      <Avatar className="h-32 w-32 border-4 border-white shadow-lg">
                        <AvatarImage src={tutor.profile_photo_url || tutor.picture} alt={tutor.name} />
                        <AvatarFallback className="bg-indigo-100 text-indigo-700 text-4xl">
                          {tutor.name?.charAt(0) || 'T'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 pt-4 sm:pt-8">
                        <h1 className="font-heading text-3xl font-semibold text-slate-900">
                          {tutor.name}
                        </h1>
                        <div className="flex flex-wrap items-center gap-4 mt-2">
                          <div className="flex items-center gap-1 text-amber-500">
                            <Star className="h-5 w-5 fill-current" />
                            <span className="font-semibold">{tutor.rating_average?.toFixed(1) || '0.0'}</span>
                            <span className="text-slate-400">
                              ({tutor.total_reviews || 0} reviews)
                            </span>
                          </div>
                          <div className="flex items-center gap-1 text-slate-500">
                            <BookOpen className="h-5 w-5" />
                            <span>{tutor.total_sessions || 0} sessions</span>
                          </div>
                          {tutor.location && (
                            <div className="flex items-center gap-1 text-slate-500">
                              <MapPin className="h-5 w-5" />
                              <span>{tutor.location}</span>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2 mt-4">
                          {tutor.subjects?.map((subject) => (
                            <Badge key={subject} className="bg-indigo-50 text-indigo-700 hover:bg-indigo-100">
                              {subject}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Introduction Video */}
            {videoEmbedUrl && (
              <Card className="border-stone-100">
                <CardHeader>
                  <CardTitle className="font-heading text-xl flex items-center gap-2">
                    <Play className="h-5 w-5 text-indigo-700" />
                    Introduction Video
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="aspect-video rounded-lg overflow-hidden bg-slate-100">
                    <iframe
                      src={videoEmbedUrl}
                      title="Tutor Introduction"
                      className="w-full h-full"
                      allowFullScreen
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* About / Bio */}
            <Card className="border-stone-100">
              <CardHeader>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <User className="h-5 w-5 text-indigo-700" />
                  About Me
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 leading-relaxed whitespace-pre-wrap">
                  {tutor.bio || 'No bio available.'}
                </p>
              </CardContent>
            </Card>

            {/* Teaching Style */}
            {tutor.teaching_style && (
              <Card className="border-stone-100">
                <CardHeader>
                  <CardTitle className="font-heading text-xl flex items-center gap-2">
                    <Briefcase className="h-5 w-5 text-indigo-700" />
                    Teaching Style & Approach
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 leading-relaxed whitespace-pre-wrap">
                    {tutor.teaching_style}
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Educational Background */}
            <Card className="border-stone-100">
              <CardHeader>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <GraduationCap className="h-5 w-5 text-indigo-700" />
                  Educational Background
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 leading-relaxed whitespace-pre-wrap">
                  {tutor.educational_background || tutor.qualifications || 'No educational background listed.'}
                </p>
              </CardContent>
            </Card>

            {/* Qualifications & Certifications */}
            <Card className="border-stone-100">
              <CardHeader>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <Award className="h-5 w-5 text-indigo-700" />
                  Certifications & Qualifications
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 leading-relaxed whitespace-pre-wrap">
                  {tutor.qualifications || 'No qualifications listed.'}
                </p>
                {tutor.experience_years > 0 && (
                  <div className="mt-4 flex items-center gap-2 text-slate-700 bg-green-50 p-3 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="font-medium">{tutor.experience_years} years of teaching experience</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Languages & Grade Levels */}
            <div className="grid sm:grid-cols-2 gap-6">
              <Card className="border-stone-100">
                <CardHeader>
                  <CardTitle className="font-heading text-xl flex items-center gap-2">
                    <Globe className="h-5 w-5 text-indigo-700" />
                    Languages Spoken
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {tutor.languages?.length > 0 ? (
                      tutor.languages.map((lang) => (
                        <Badge key={lang} variant="outline" className="py-2 px-4">
                          {lang}
                        </Badge>
                      ))
                    ) : (
                      <Badge variant="outline" className="py-2 px-4">English</Badge>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="border-stone-100">
                <CardHeader>
                  <CardTitle className="font-heading text-xl">Teaching Levels</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {tutor.grade_levels?.map((level) => (
                      <Badge key={level} variant="outline" className="py-2 px-4">
                        {gradeLevelNames[level] || level}
                      </Badge>
                    ))}
                    {(!tutor.grade_levels || tutor.grade_levels.length === 0) && (
                      <p className="text-slate-500">No levels specified</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Availability Schedule */}
            <Card className="border-stone-100">
              <CardHeader>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-indigo-700" />
                  Weekly Availability
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(() => {
                  const availabilityData = getAvailabilityDisplay(tutor.availability);
                  const nextAvailable = formatNextAvailable(tutor.availability);
                  
                  if (availabilityData.length === 0) {
                    return (
                      <div className="text-center py-6">
                        <Clock className="h-12 w-12 text-slate-200 mx-auto mb-2" />
                        <p className="text-slate-500">Contact admin for scheduling information</p>
                      </div>
                    );
                  }
                  
                  return (
                    <div className="space-y-4">
                      {/* Next Available Highlight */}
                      {nextAvailable && (
                        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
                          <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                            <CheckCircle className="h-5 w-5 text-green-600" />
                          </div>
                          <div>
                            <p className="text-xs font-medium text-green-700 uppercase tracking-wide">Next Available</p>
                            <p className="text-lg font-semibold text-green-800">{nextAvailable}</p>
                          </div>
                        </div>
                      )}
                      
                      {/* Weekly Schedule */}
                      <div className="grid gap-2">
                        {availabilityData.map(({ key, dayFull, timeRange }) => (
                          <div 
                            key={key}
                            className="flex items-center justify-between p-3 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors"
                          >
                            <span className="font-medium text-slate-700 w-28">{dayFull}</span>
                            <span className="text-slate-600">{timeRange}</span>
                          </div>
                        ))}
                      </div>
                      
                      {tutor.availability_summary && (
                        <p className="text-sm text-slate-500 italic mt-4 p-3 bg-slate-50 rounded-lg">
                          {tutor.availability_summary}
                        </p>
                      )}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            {/* Reviews */}
            <Card className="border-stone-100">
              <CardHeader>
                <CardTitle className="font-heading text-xl flex items-center gap-2">
                  <Star className="h-5 w-5 text-amber-500" />
                  Student Reviews ({tutor.total_reviews || 0})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {tutor.reviews?.length > 0 ? (
                  <div className="space-y-6">
                    {tutor.reviews.map((review) => (
                      <div key={review.review_id} className="border-b border-stone-100 pb-6 last:border-0 last:pb-0">
                        <div className="flex items-center gap-3 mb-2">
                          <Avatar className="h-10 w-10">
                            <AvatarFallback className="bg-slate-100">
                              {review.student_name?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-slate-900">{review.student_name}</p>
                            <div className="flex items-center gap-1">
                              {[...Array(5)].map((_, i) => (
                                <Star 
                                  key={i} 
                                  className={`h-4 w-4 ${i < review.rating ? 'text-amber-500 fill-current' : 'text-slate-200'}`} 
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                        <p className="text-slate-600 pl-13">{review.comment}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Star className="h-12 w-12 text-slate-200 mx-auto mb-2" />
                    <p className="text-slate-500">No reviews yet. Be the first to leave a review!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card className="border-stone-100 sticky top-24">
              <CardContent className="p-6">
                <div className="text-center mb-6">
                  <div className="flex items-center justify-center gap-1 mb-2">
                    <DollarSign className="h-6 w-6 text-slate-900" />
                    <span className="text-4xl font-bold text-slate-900">
                      {tutor.hourly_rate?.toFixed(0) || 0}
                    </span>
                  </div>
                  <p className="text-slate-500">per hour</p>
                </div>

                <Button 
                  onClick={handleBookSession}
                  className="w-full rounded-full bg-slate-900 hover:bg-slate-800 py-6 text-lg"
                  data-testid="sidebar-book-btn"
                >
                  Book a Session
                </Button>

                {isAuthenticated && (
                  <Button 
                    variant="outline" 
                    className="w-full rounded-full mt-3"
                    onClick={() => setMessageOpen(true)}
                    data-testid="sidebar-message-btn"
                  >
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Send Message
                  </Button>
                )}

                <div className="mt-6 pt-6 border-t border-stone-100 space-y-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Response time</span>
                    <span className="text-slate-900 font-medium">Within 24 hours</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Sessions completed</span>
                    <span className="text-slate-900 font-medium">{tutor.total_sessions || 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Experience</span>
                    <span className="text-slate-900 font-medium">{tutor.experience_years || 0} years</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Rating</span>
                    <span className="text-slate-900 font-medium flex items-center gap-1">
                      <Star className="h-4 w-4 text-amber-500 fill-current" />
                      {tutor.rating_average?.toFixed(1) || '0.0'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Location</span>
                    <span className="text-slate-900 font-medium">{tutor.location || 'Online'}</span>
                  </div>
                </div>

                {/* Quick Info Badges */}
                <div className="mt-6 pt-6 border-t border-stone-100">
                  <p className="text-sm font-medium text-slate-700 mb-3">Languages</p>
                  <div className="flex flex-wrap gap-2">
                    {(tutor.languages || ['English']).map((lang) => (
                      <Badge key={lang} variant="secondary" className="text-xs">
                        {lang}
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TutorProfilePage;
